import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field, Logo, Notice, Spinner } from '../components/ui';
import { ApiError } from '../lib/api';
import { useMe, useSession } from '../lib/session';
import { parseEmails, useInvite, usePeople } from '../lib/people';
import { GetTheApp } from '../components/GetTheApp';
import {
  completeOnboarding,
  getFrameworks,
  getProfile,
  JOB_TITLES,
  getRoleOptions,
  saveJobTitle,
  settingOptions,
  suggestionsFor,
  areasOf,
  type PriorityArea,
  saveProfile,
  setDefaultFramework,
} from '../lib/onboarding';
import { CountryField, EducationSettingField, PriorityAreasField, ChipChoice, STAFF_LABEL } from '../components/OrgFields';
import { CUSTOM_FRAMEWORK_NOTE, FrameworkOutline } from './Frameworks';
import { useScoreMode, type ScoreMode } from '../lib/hub';
import { ScoreModeAlways, ScoreModeOptions } from '../components/ScoreMode';
import { TermDatesField, termDatesProblem, type TermDate } from '../components/TermDates';
import './Welcome.css';

const STEPS = ['Organisation', 'You', 'Framework', 'Priority areas', 'Agree', 'Team', 'App', 'Terms'] as const;
/** The steps each kind of account goes through (by id, in order): one person's account skips the organisation, terms and team ones. */
// Someone on their own answers what shapes their feedback: about them, their
// role, framework and what they want to work on. No team, scores or terms
// (their term dates default to their country's).
const ORDER = { organisation: [0, 1, 2, 3, 7, 4, 5, 6], personal: [0, 1, 2, 3, 4, 6] } as const;
const PERSONAL_LABELS: Partial<Record<number, string>> = { 0: 'About you', 3: 'Your focus' };

/**
 * First-time setup for a new organisation, about three minutes. Each field is here
 * because the Leadership Hub or the coaching uses it, so every step needs an
 * answer (the server checks too). Only the trust name and inviting the team
 * are optional; everything can be changed later.
 */
export function WelcomePage() {
  const me = useMe();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: ['schoolProfile'], queryFn: getProfile });
  const frameworks = useQuery({ queryKey: ['frameworkOptions'], queryFn: getFrameworks });
  const roleOptions = useQuery({ queryKey: ['roleOptions'], queryFn: getRoleOptions });
  const jobTitles = roleOptions.data ?? JOB_TITLES;
  const invite = useInvite();
  // Seats EducAitor set for the organisation (none: no limit). The owner already uses one.
  const people = usePeople();
  const seatsLeft = people.data?.seats.available ?? null;
  const seatLimit = people.data?.seats.limit ?? null;
  const isOwner = !!people.data?.me.isOwner;

  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState<string | null>(null);
  const [phaseOther, setPhaseOther] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [countryOther, setCountryOther] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [staffBand, setStaffBand] = useState<string | null>(null);
  const [trustName, setTrustName] = useState('');
  const [workplace, setWorkplace] = useState('');
  const [jobTitle, setJobTitle] = useState(me.job_title ?? '');
  const [otherTitle, setOtherTitle] = useState('');
  const [frameworkId, setFrameworkId] = useState<string | null>(null);
  const [priorities, setPriorities] = useState<PriorityArea[]>([]);
  const [terms, setTerms] = useState<TermDate[]>([]);
  const [emails, setEmails] = useState('');
  const [agreed, setAgreed] = useState(false);
  // How the organisation uses scores (owners choose; required).
  const [scores, setScores] = useState<ScoreMode | null>(null);
  const scoreMode = useScoreMode();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const p = profile.data;
    if (!p) return;
    setPhase(p.phase);
    setPhaseOther(p.phaseOther ?? '');
    setCountry(p.country ?? 'England');
    setStaffBand(p.staffBand);
    setTrustName(p.trustName ?? '');
    setWorkplace(p.workplace ?? '');
    setPriorities(areasOf(p));
    setTerms(p.termDates ?? []);
  }, [profile.data]);

  // Start on the school's current framework (older schools already have one), else the first.
  useEffect(() => {
    if (frameworkId || !frameworks.data?.length || profile.isLoading) return;
    const current = profile.data?.defaultFrameworkId;
    setFrameworkId(frameworks.data.some((f) => f.id === current) ? current! : frameworks.data[0].id);
  }, [frameworks.data, frameworkId, profile.data, profile.isLoading]);

  const inviteList = useMemo(() => parseEmails(emails), [emails]);
  // A typed country must be one from the list (older APIs send no list, so anything goes).
  const countryOk = (c: string | null) => {
    const all = profile.data?.options.allCountries;
    return !!c && (!all || all.includes(c));
  };
  // Setup feeds the Hub, so each step needs an answer before moving on.
  // Only the trust name and inviting the team are optional.
  const answered = [
    !!phase && (phase !== 'other' || !!phaseOther.trim()) && countryOk(country) && (profile.data?.type === 'personal' || !!staffBand),
    !!jobTitle && (jobTitle !== 'Other' || !!otherTitle.trim()),
    !!frameworkId,
    priorities.some((p) => p.text.trim()),
    agreed && (profile.data?.type === 'personal' || !isOwner || !!scores),
    seatsLeft == null || inviteList.length <= seatsLeft,
    true,
    !termDatesProblem(terms),
  ];
  const schoolName = me.school_name ?? 'your organisation';
  const personal = profile.data?.type === 'personal';
  const order: readonly number[] = personal ? ORDER.personal : ORDER.organisation;
  const at = Math.max(0, order.indexOf(step));
  const goNext = () => setStep(order[Math.min(at + 1, order.length - 1)]);
  const goBack = () => setStep(order[Math.max(at - 1, 0)]);
  // A personal account starts on its first step, not the organisation one.
  useEffect(() => {
    if (!order.includes(step)) setStep(order[0]);
  }, [order, step]);

  const next = async () => {
    setError(null);
    setBusy(true);
    try {
      if (step === 0) await saveProfile(personal ? { phase, phaseOther, country, workplace } : { phase, phaseOther, country, staffBand, trustName });
      if (step === 1) await saveJobTitle(jobTitle === 'Other' ? otherTitle : jobTitle);
      if (step === 2 && frameworkId) await setDefaultFramework(frameworkId);
      if (step === 3) await saveProfile({ priorities: priorities.filter((p) => p.text.trim()) });
      if (step === 7) await saveProfile({ termDates: terms.map((t) => ({ ...t, name: t.name.trim() })) });
      if (step === 4) {
        // Terms first: inviting staff shares their emails with us.
        await completeOnboarding();
        if (!personal && isOwner && scores) await scoreMode.mutateAsync(scores);
      }
      if (step === 5 && inviteList.length) await invite.mutateAsync({ emails: inviteList, role: 'teacher' });
      if (step === 6) {
        await qc.invalidateQueries();
        await refresh();
        navigate('/', { replace: true });
        return;
      }
      goNext();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (profile.isLoading) {
    return (
      <div className="welcome-loading">
        <Spinner />
      </div>
    );
  }
  const options = profile.data?.options;

  return (
    <div className="welcome">
      <header className="welcome-top">
        <Logo height={26} />
        <ol className="welcome-steps" aria-label="Setup progress">
          {order.map((id, i) => (
            <li key={id} className={i === at ? 'is-current' : i < at ? 'is-done' : undefined} aria-current={i === at ? 'step' : undefined}>
              <span className="welcome-step-dot" />
              <span className="welcome-step-label">{(personal && PERSONAL_LABELS[id]) || STEPS[id]}</span>
            </li>
          ))}
        </ol>
      </header>

      <main className="welcome-main" id="main">
        {step === 0 && personal ? (
          <Step title="Tell us about your teaching" lede="So your feedback fits where and who you teach.">
            <EducationSettingField options={settingOptions(profile.data)} value={phase} other={phaseOther} onChange={setPhase} onOtherChange={setPhaseOther} />
            <CountryField
              quick={options?.countries ?? []}
              all={options?.allCountries ?? options?.countries ?? []}
              value={country}
              onChange={setCountry}
              otherOpen={countryOther}
              onOtherOpen={setCountryOther}
            />
            <Field
              id="welcome-workplace"
              label="Where do you work? (optional)"
              placeholder="e.g. Oakfield Primary School"
              value={workplace}
              maxLength={255}
              onChange={(e) => setWorkplace(e.target.value)}
            />
          </Step>
        ) : step === 0 ? (
          <Step title="Tell us about your organisation" lede={`${schoolName}. This shapes the language and comparisons you'll see later.`}>
            <EducationSettingField options={settingOptions(profile.data)} value={phase} other={phaseOther} onChange={setPhase} onOtherChange={setPhaseOther} />
            <CountryField
              quick={options?.countries ?? []}
              all={options?.allCountries ?? options?.countries ?? []}
              value={country}
              onChange={setCountry}
              otherOpen={countryOther}
              onOtherOpen={setCountryOther}
            />
            <ChipChoice label={STAFF_LABEL} options={(options?.staffBands ?? []).map((v) => ({ value: v, label: v }))} value={staffBand} onChange={setStaffBand} />
            <Field
              id="welcome-trust"
              label="Part of a trust or group? (optional)"
              placeholder="e.g. Northern Lights Academy Trust"
              value={trustName}
              onChange={(e) => setTrustName(e.target.value)}
            />
          </Step>
        ) : null}

        {step === 1 ? (
          <Step title="What's your role?" lede={personal ? 'So your feedback and coaching suit what you do.' : 'So we send the right summaries to the right people.'}>
            <Choice
              label="Your role"
              options={[...jobTitles, 'Other'].map((t) => ({ value: t, label: t }))}
              value={jobTitle && !jobTitles.includes(jobTitle) && jobTitle !== 'Other' ? 'Other' : jobTitle}
              onChange={(v) => setJobTitle(v)}
              stacked
            />
            {jobTitle === 'Other' ? (
              <Field id="welcome-title" label="Your job title" value={otherTitle} onChange={(e) => setOtherTitle(e.target.value)} autoFocus />
            ) : null}
          </Step>
        ) : null}

        {step === 2 ? (
          <Step title="Which framework do you teach to?" lede={`Lessons are scored against it. You can switch later in Frameworks. ${CUSTOM_FRAMEWORK_NOTE}`}>
            {frameworks.isLoading ? (
              <Spinner />
            ) : (
              <div className="option-cards" role="radiogroup" aria-label="Framework">
                {(frameworks.data ?? []).map((f) => (
                  <button key={f.id} type="button" role="radio" aria-checked={frameworkId === f.id} className="option-card" onClick={() => setFrameworkId(f.id)}>
                    <span className="option-radio" aria-hidden="true" />
                    <span>
                      <strong>{f.name}</strong>
                      {f.description ? <span className="muted option-desc">{f.description}</span> : null}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {frameworkId ? <FrameworkOutline id={frameworkId} open={previewOpen} onToggle={() => setPreviewOpen((v) => !v)} /> : null}
          </Step>
        ) : null}

        {step === 3 ? (
          <Step
            title={personal ? 'What do you want to work on?' : 'What are your Leadership Priority Areas?'}
            lede={
              personal
                ? 'Up to three areas. We suggest focuses in these first, and you can see how they move over time.'
                : 'Up to three. The Leadership Hub will track how your team develops in these first.'
            }
          >
            <PriorityAreasField areas={priorities} onChange={setPriorities} suggestions={suggestionsFor(profile.data, phase)} personal={personal} />
          </Step>
        ) : null}

        {step === 7 ? (
          <Step
            title="When are your terms?"
            lede="The Leadership Hub compares term with term, and teachers look back on each term, so we use your dates rather than guessing. You can change them, and add next year's, in organisation settings."
          >
            <TermDatesField value={terms} onChange={setTerms} />
            {terms.length && termDatesProblem(terms) ? <p className="faint">{termDatesProblem(terms)}</p> : null}
          </Step>
        ) : null}

        {step === 5 && seatsLeft === 0 ? (
          <Step title="Your team" lede={`${schoolName} has ${seatLimit} seat${seatLimit === 1 ? '' : 's'}, and ${seatLimit === 1 ? 'you’re using it' : 'they’re all in use'}.`}>
            <p className="muted">To invite colleagues, ask EducAitor for more seats. You can invite people from People once you have them.</p>
          </Step>
        ) : step === 5 ? (
          <Step
            title="Invite your team"
            lede={`Paste staff email addresses. Each gets an email with a button that opens the app and joins, with no code needed.${
              seatsLeft != null ? ` You have ${seatsLeft} seat${seatsLeft === 1 ? '' : 's'} left.` : ''
            } You can do this later from People.`}
          >
            <label htmlFor="welcome-invites" className="sr-label">
              Email addresses
            </label>
            <textarea
              id="welcome-invites"
              className="textarea"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder={'jane.smith@school.org\nsam.patel@school.org'}
            />
            {seatsLeft != null && inviteList.length > seatsLeft ? (
              <Notice tone="error">
                That&rsquo;s {inviteList.length} people and you have {seatsLeft} seat{seatsLeft === 1 ? '' : 's'} left. Remove {inviteList.length - seatsLeft}, or
                ask EducAitor for more seats.
              </Notice>
            ) : (
              <p className="faint">
                {inviteList.length} {inviteList.length === 1 ? 'address' : 'addresses'}. Everyone joins as a teacher; you can make people leaders from People.
              </p>
            )}
          </Step>
        ) : null}

        {step === 6 ? (
          <Step
            title="Get the EducAitor app"
            lede={`Lessons are recorded in the app on your phone, and your feedback appears there too.${
              personal ? '' : ' This web app is where you manage your organisation.'
            }`}
          >
            <GetTheApp email={me.email} />
          </Step>
        ) : null}

        {step === 4 ? (
          <Step
            title={personal ? 'Before you start' : 'Before your team joins'}
            lede={personal ? 'How EducAitor looks after your lessons.' : 'How EducAitor keeps development separate from judgement.'}
          >
            <ul className="promises">
              {personal ? (
                <li>
                  <strong>Your lesson reports are private.</strong> Nobody else reads them unless you choose to share one. Your scores are yours alone.
                </li>
              ) : (
                <>
                  <li>
                    <strong>Teachers&rsquo; lesson reports are private.</strong> You only see a report if a teacher chooses to share it with you.
                  </li>
                  <li>
                    {scores === 'full' ? (
                      <>
                        <strong>You&rsquo;ll see your team&rsquo;s development, and each teacher&rsquo;s score trend from the lessons they share.</strong> Teachers
                        are told, and it&rsquo;s never a ranking.
                      </>
                    ) : scores === 'trends' ? (
                      <>
                        <strong>You&rsquo;ll see your team&rsquo;s development as trends, not numbers.</strong> Strengths, next steps and whether each area is
                        improving, never a ranking.
                      </>
                    ) : (
                      <>
                        <strong>You&rsquo;ll see your team&rsquo;s development, not individuals&rsquo; scores.</strong> Patterns, strengths and next steps across the
                        team, never a ranking.
                      </>
                    )}
                  </li>
                  <li>
                    <strong>Small groups stay anonymous.</strong> Figures only appear for groups of five or more.
                  </li>
                </>
              )}
              <li>
                <strong>Lesson audio is deleted</strong> as soon as the report is written.
              </li>
            </ul>
            {!personal && isOwner ? (
              <fieldset className="choice">
                <legend>How should scores be shown?</legend>
                <p className="muted">
                  Every lesson gets written feedback: strengths, next steps and quotes from the lesson. Choose who also sees the numbers. You can change this later
                  in organisation settings, and your team is told if you do.
                </p>
                <ScoreModeOptions value={scores} onChange={setScores} />
                <ScoreModeAlways />
              </fieldset>
            ) : null}
            <label className="agree">
              <input id="welcome-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>
                I agree to the{' '}
                <a href="https://educaitor.ai/terms/" target="_blank" rel="noreferrer">
                  Terms
                </a>{' '}
                and{' '}
                <a href="https://educaitor.ai/privacy-policy/" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                {personal ? '.' : ` on behalf of ${schoolName}.`}
              </span>
            </label>
          </Step>
        ) : null}

        {error ? <Notice tone="error">{error}</Notice> : null}

        <div className="welcome-actions">
          {at > 0 ? (
            <Button variant="ghost" onClick={goBack} disabled={busy}>
              Back
            </Button>
          ) : (
            <span />
          )}
          <div className="welcome-actions-right">
            {step === 5 && seatsLeft !== 0 ? (
              <Button variant="secondary" disabled={busy} onClick={() => setStep(6)}>
                Do this later
              </Button>
            ) : null}
            <Button onClick={next} loading={busy} disabled={!answered[step]}>
              {step === 4
                ? 'Agree and continue'
                : step === 5 && inviteList.length && seatsLeft !== 0
                  ? `Send ${inviteList.length} invite${inviteList.length === 1 ? '' : 's'}`
                  : step === 6
                    ? 'Go to EducAitor'
                    : 'Continue'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Step({ title, lede, children }: { title: string; lede: string; children: ReactNode }) {
  return (
    <section className="welcome-step">
      <div className="welcome-head">
        <h1>{title}</h1>
        <p className="muted">{lede}</p>
      </div>
      {children}
    </section>
  );
}

export function Choice({
  label,
  options,
  value,
  onChange,
  stacked,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (v: string) => void;
  stacked?: boolean;
}) {
  return (
    <fieldset className="choice">
      <legend>{label}</legend>
      <div className={stacked ? 'chips chips-stacked' : 'chips'}>
        {options.map((o) => (
          <button key={o.value} type="button" className="choice-chip" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}
